import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import AdminDataModal, { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type FooterStatus = "active" | "inactive";

interface FooterRow {
  id: number;
  section: string;
  title: string;
  content: string;
  link: string;
  sort_order: number;
  status: FooterStatus;
  createdAt: string;
  updatedAt: string;
}

type ModalMode = "add" | "edit" | "view";

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";

const defaultRowData = {
  id: "",
  section: "",
  title: "",
  content: "",
  link: "",
  sort_order: 0,
  status: "active",
};

const AdminFooter: React.FC = () => {
  const { showModal } = useMessageModal();

  const [rows, setRows] = useState<FooterRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<FooterRow | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [modalRowData, setModalRowData] = useState<any>(defaultRowData);

  const fetchFooter = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/footer`, {
        withCredentials: true,
      });

      setRows(response.data?.data || []);
    } catch (error) {
      console.error("fetchFooter error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Unable to fetch footer records.",
      });
    }
  };

  useEffect(() => {
    fetchFooter();
  }, []);

  const handleAdd = () => {
    setModalMode("add");
    setModalRowData(defaultRowData);
    setIsModalOpen(true);
  };

  const handleView = (row: FooterRow) => {
    setModalMode("view");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleEdit = (row: FooterRow) => {
    setModalMode("edit");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row: FooterRow) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${row.title}"?`,
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/footer/${row.id}`, {
        withCredentials: true,
      });

      showModal({
        type: "success",
        title: "Success",
        message: "Footer record deleted successfully.",
      });

      setSelectedRow(null);
      fetchFooter();
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message || "Unable to delete footer record.",
      });
    }
  };

  const handleSubmit = async (formData: any) => {
    if (!formData.section?.trim() || !formData.title?.trim()) {
      showModal({
        type: "confirm",
        title: "Validation",
        message: "Section and title are required.",
      });
      return;
    }

    try {
      const payload = {
        section: formData.section,
        title: formData.title,
        content: formData.content,
        link: formData.link,
        sort_order: formData.sort_order || 0,
        status: formData.status || "active",
      };

      if (modalMode === "add") {
        await axios.post(`${API_URL}/admin/footer`, payload, {
          withCredentials: true,
        });
      } else {
        await axios.put(`${API_URL}/admin/footer/${formData.id}`, payload, {
          withCredentials: true,
        });
      }

      showModal({
        type: "success",
        title: "Success",
        message: `Footer record ${
          modalMode === "add" ? "added" : "updated"
        } successfully.`,
      });

      setIsModalOpen(false);
      setSelectedRow(null);
      setModalRowData(defaultRowData);
      fetchFooter();
    } catch (error: any) {
      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message || "Unable to save footer record.",
      });
    }
  };

  const adminFooterFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID" },
      {
        key: "section",
        label: "Section",
        type: "select",
        options: [
          { label: "About Us", value: "about" },
          { label: "Useful Links", value: "useful_links" },
          { label: "Recent Activities", value: "recent_activities" },
          { label: "Get In Touch", value: "contact" },
          { label: "Footer Bottom", value: "bottom" },
        ],
      },
      { key: "title", label: "Title", type: "text" },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
      },
      { key: "link", label: "Link", colSpan: 2 },
      {
        key: "content",
        label: "Content",
        type: "textarea",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "section", label: "Section" },
    { key: "title", label: "Title" },
    {
      key: "content",
      label: "Content",
      render: (row: FooterRow) => (
        <span className="admin-testimonial-text-clamp">{row.content}</span>
      ),
    },
    {
      key: "link",
      label: "Link",
      render: (row: FooterRow) => (
        <span className="admin-testimonial-text-clamp">{row.link || "-"}</span>
      ),
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "status",
      label: "Status",
      render: (row: FooterRow) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row: FooterRow) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
    },
  ];

  return (
    <div className="admin-page-wrap">
      <AdminListToolkit
        title="Footer"
        columns={columns}
        data={rows}
        selectedRow={selectedRow}
        onAdd={handleAdd}
        onView={handleView}
        onEdit={handleEdit}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="footer"
      >
        {(filteredData: any) => {
          const dataArray = Array.isArray(filteredData)
            ? filteredData
            : filteredData?.data || [];

          return (
            <DataTable
              columns={columns}
              data={dataArray}
              selectedRowId={selectedRow?.id ?? null}
              onRowSelect={(row: FooterRow) => setSelectedRow(row)}
            />
          );
        }}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={isModalOpen}
        mode={modalMode}
        title="Footer"
        fields={adminFooterFields}
        rowData={modalRowData}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onFormChange={setModalRowData}
      />
    </div>
  );
};

export default AdminFooter;
